/**
 * The thirteen sections FR-I14 adds — a reader and a default renderer each,
 * in the shape [ADR-0020](../../../../docs/adr/0020-a-template-owns-its-section-markup.md)
 * set and [ADR-0022](../../../../docs/adr/0022-a-section-is-a-block-and-the-nine-stay.md)
 * restates.
 *
 * `page_hero` · `showcase` · `metrics` · `split_feature` · `product_spotlight`
 * `feature_grid` · `logo_strip` · `testimonial_strip` · `statement` · `steps`
 * `story_strip` · `lead_form` · `cta`
 *
 * ## The reader holds the rules, the renderer holds nothing but markup
 *
 * Each section is two functions. `readX(props, ctx)` turns the payload into a
 * view-model and is **the only place a rule is applied** — FR-I4's price,
 * ADR-0014's conduct switches, the escaping of a hostile link, the emptiness
 * test. `X(view)` turns the view-model into markup and can see nothing the
 * reader removed, which is the property that makes it safe for eight templates
 * to redraw a section a regulator governs (ADR-0022 §7).
 *
 * A theme that names none of these still draws all thirteen, because
 * `index.tsx: SHARED` carries the defaults below.
 *
 * ## Four rules, each already paid for once
 *
 * 1. **A section renders nothing rather than something wrong.** Every reader
 *    returns `null` when its content is absent, and `draw()` renders nothing
 *    for a `null` view on every template alike. An empty box on a firm's
 *    public page is worse than no section: the firm cannot see it (it looks
 *    like a section it has not filled in yet) and a visitor reads it as a site
 *    that is broken.
 * 2. **Every picture sits in a reserved box** (Wave 3 close §6.1). An `<img>`
 *    with no dimensions has no box until its bytes arrive, so everything below
 *    it jumps the moment they do — on a phone, mid-read.
 * 3. **No section invents copy, an icon that means something, or a claim**
 *    (ADR-0020 §6). Nothing below writes a word that is not the firm's. `steps`
 *    numbers itself from the position and `metrics` prints what the firm typed;
 *    neither computes anything and neither draws a symbol that says something
 *    about the work.
 * 4. **A section that reads live data reads it at render.** `product_spotlight`
 *    holds service *slugs* and the service resolves them against the catalog on
 *    every render (`_resolve_spotlight_block`), so a firm that renames a service
 *    has renamed it on its website. `lead_form`'s select is resolved the same
 *    way.
 */

import type { ReactNode } from 'react'

import { iconKey, SiteIcon } from '../icons'
import { list, num, obj, rupees, src, str } from '../read'
import { Buttons, link, readButtons, Section, type BlockContext, type SiteButton } from './common'
import { EnquiryForm, enquiryProps, type EnquiryFormProps } from './enquiry-form'
import { hasRichText, RichTextBody } from './rich'

/**
 * Whether a conduct switch allows a section at all — **failing closed**
 * (ADR-0014 §3, ADR-0022 §6).
 *
 * Anything that is not exactly `true` is `false`: a missing policy object, a
 * `null`, a switch the service never sent, a payload from a fixture. There is
 * no third way to render a testimonial, and the failure that matters is the
 * one where a firm's page carries what its regulator forbids.
 *
 * The service has already dropped these sections from the payload
 * (`SUPPRESSED_BY_SWITCH`) — hidden in the markup is still in the page source.
 * This is the second guard, and it is what lets this half *test* the rule.
 */
function allowed(ctx: BlockContext, switchName: string): boolean {
  return ctx.switches?.[switchName] === true
}

/* ── page_hero ─────────────────────────────────────────────────────────── */

export interface PageHeroView {
  eyebrow: string | null
  heading: string | null
  subheading: string | null
  body: string | null
  alignment: 'left' | 'centre'
  /** C20, ADR-0041 §1 — the firm's buttons, zero to two. */
  buttons: SiteButton[]
}

export function readPageHero(props: Record<string, unknown>, ctx: BlockContext): PageHeroView | null {
  const heading = str(props.heading)
  const subheading = str(props.subheading)
  // An eyebrow over nothing is a label for a section that is not there.
  if (!heading && !subheading) return null
  return {
    eyebrow: str(props.eyebrow),
    heading,
    subheading,
    body: str(props.body),
    alignment: props.alignment === 'centre' ? 'centre' : 'left',
    buttons: readButtons(props.buttons, ctx),
  }
}

/**
 * The opening band of an inside page. **No picture**: the full-height band
 * with a photograph is `hero`, and a page with two of those is a page with two
 * beginnings. Buttons only when the firm gave it some (C20, ADR-0041 §1).
 *
 * `<h1>`, because on the page this section was drawn for it *is* the page's
 * heading. A firm that puts it under a `hero` gets two — which is untidy and
 * not an error, and the schema cannot tell the two arrangements apart.
 */
export function PageHero(view: PageHeroView): ReactNode {
  return (
    <section className={`block page-hero page-hero-${view.alignment}`}>
      <div className="wrap">
        {view.eyebrow ? <p className="eyebrow">{view.eyebrow}</p> : null}
        {view.heading ? <h1>{view.heading}</h1> : null}
        {view.subheading ? <p className="lede">{view.subheading}</p> : null}
        {view.body ? <p className="page-hero-body">{view.body}</p> : null}
        <Buttons className="page-hero-actions" buttons={view.buttons} />
      </div>
    </section>
  )
}

/* ── showcase ──────────────────────────────────────────────────────────── */

export interface ShowcaseStep {
  name: string
  note: string | null
  /** C21 (ADR-0043 §2): the firm's category for this item, or `null`. */
  category: string | null
}

export interface ShowcaseView {
  heading: string
  subheading: string | null
  body: string | null
  buttons: SiteButton[]
  steps: ShowcaseStep[]
  /**
   * C21 (ADR-0043 §2, I-1): the distinct categories of the items, in the order
   * they first appear, at most `SHOWCASE_MAX_CATEGORIES` — the chips a
   * filterable showcase draws after *All*.
   */
  categories: string[]
  /** C21: the firm switched the chips on, and there is a category to filter by. */
  filterable: boolean
  /**
   * *Additive to I-1*, as `ScheduleView.group` is: the chips' radio group and
   * id prefix, `show<block index>`, unique on the page.
   */
  group: string
}

/**
 * The most categories a showcase's chips name. `LIBRARY_TWO_CSS` carries one
 * rule per category, so the number is the stylesheet's as much as the
 * reader's; an item in a ninth category still shows under *All*.
 */
export const SHOWCASE_MAX_CATEGORIES = 8

export function readShowcase(props: Record<string, unknown>, ctx: BlockContext, index = 0): ShowcaseView | null {
  const heading = str(props.heading)
  if (!heading) return null
  const steps = list(props.steps)
    .map((raw) => {
      const item = obj(raw)
      const name = str(item.name)
      return name ? { name, note: str(item.note), category: str(item.category) } : null
    })
    .filter((s): s is ShowcaseStep => s !== null)
  const categories: string[] = []
  for (const step of steps) {
    if (step.category && !categories.includes(step.category) && categories.length < SHOWCASE_MAX_CATEGORIES) {
      categories.push(step.category)
    }
  }
  return {
    heading,
    subheading: str(props.subheading),
    body: str(props.body),
    buttons: readButtons(props.buttons, ctx),
    steps,
    categories,
    filterable: props.filterable === true && categories.length > 0,
    group: `show${index}`,
  }
}

/**
 * C21's chips (ADR-0043 §2): **a radio group and `:has()`**, as `tabs` is.
 * *All* is first and checked; each category's radio hides the items of every
 * other category, by the `showcase-cat-<n>` class the item carries. With no
 * CSS, or where `:has()` is missing, the chips are hidden and every item shows.
 */
function ShowcaseChips({ view }: { view: ShowcaseView }): ReactNode {
  const { group, categories } = view
  const chip = (i: number, label: string) => [
    <input
      key={`r${i}`}
      type="radio"
      className="showcase-chip-radio"
      name={group}
      id={`${group}-c${i}`}
      defaultChecked={i === 0}
    />,
    <label key={`l${i}`} className="showcase-chip" htmlFor={`${group}-c${i}`}>
      {label}
    </label>,
  ]
  return <div className="showcase-chips">{[chip(0, 'All'), ...categories.map((c, i) => chip(i + 1, c))]}</div>
}

export function Showcase(view: ShowcaseView): ReactNode {
  const { subheading, body, steps, buttons, categories, filterable } = view
  const items = steps.length > 0 ? (
    <ul className="showcase-steps">
      {steps.map((step, i) => {
        const n = step.category ? categories.indexOf(step.category) + 1 : 0
        return (
          <li key={i} {...(n > 0 ? { className: `showcase-cat-${n}` } : {})}>
            {step.category && categories.length > 0 ? <span className="showcase-category">{step.category}</span> : null}
            <strong>{step.name}</strong>
            {step.note ? <span className="note">{step.note}</span> : null}
          </li>
        )
      })}
    </ul>
  ) : null
  return (
    <Section className={`block showcase${filterable ? ' showcase-filterable' : ''}`} heading={view.heading}>
      {subheading ? <p className="lede">{subheading}</p> : null}
      {body ? <p className="section-intro">{body}</p> : null}
      {filterable && items ? (
        <div className="showcase-filter">
          <ShowcaseChips view={view} />
          {items}
        </div>
      ) : (
        items
      )}
      <Buttons className="showcase-actions" buttons={buttons} />
    </Section>
  )
}

/* ── metrics ───────────────────────────────────────────────────────────── */

export interface MetricsView {
  heading: string | null
  intro: string | null
  layout: 'cards' | 'band'
  items: MetricItem[]
}

/** C23, ADR-0045 §4: `icon` is the firm's pick, or `null` — never ours. */
export interface MetricItem {
  value: string
  label: string
  note: string | null
  icon: string | null
}

export function readMetrics(props: Record<string, unknown>): MetricsView | null {
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const value = str(item.value)
      const label = str(item.label)
      // Both or neither: a figure with no label says nothing and a label with
      // no figure is a gap where a number should be.
      return value && label ? { value, label, note: str(item.note), icon: iconKey(item.icon) } : null
    })
    .filter((m): m is MetricItem => m !== null)
  if (items.length === 0) return null
  return {
    heading: str(props.heading),
    intro: str(props.intro),
    layout: props.layout === 'band' ? 'band' : 'cards',
    items,
  }
}

/**
 * Figures the firm states about itself. **`value` is printed exactly as the
 * firm typed it** — ProDesk computes none of it and formats none of it, because
 * a number this product produced and put on a public page would be a claim the
 * firm never made (ADR-0022 §4). No comparison. **An icon only where the firm
 * picked one** (ADR-0045 §4, which supersedes ADR-0022 §4's icon refusal).
 */
export function Metrics(view: MetricsView): ReactNode {
  const { intro, items, layout } = view
  return (
    <Section className={`block metrics metrics-${layout}`} heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <dl className="metric-list">
        {items.map((m, i) => (
          <div className="metric" key={i}>
            <SiteIcon name={m.icon} className="metric-icon" />
            <dt>{m.value}</dt>
            <dd>
              {m.label}
              {m.note ? <span className="note">{m.note}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
    </Section>
  )
}

/* ── split_feature ─────────────────────────────────────────────────────── */

export interface SplitFeatureView {
  heading: string | null
  /** The rich-text value, for `RichTextBody` — already known to be non-empty,
   *  or `null` when the firm wrote none. */
  body: unknown | null
  points: string[]
  image: { src: string; alt: string; width: number; height: number; cover: boolean } | null
  imageSide: 'left' | 'right'
  buttons: SiteButton[]
}

export function readSplitFeature(
  props: Record<string, unknown>,
  ctx: BlockContext,
): SplitFeatureView | null {
  const body = hasRichText(props.body) ? props.body : null
  const points = list(props.points)
    .map((raw) => str(obj(raw).text))
    .filter((t): t is string => t !== null)
  const source = src(props.image_url)
  // The heading alone is a band with a title and nothing under it.
  if (!body && points.length === 0 && !source) return null
  // Wave 3 close §6.1 — the box before the bytes, both ways round. The firm's
  // own dimensions when the builder captured them, so a picture chosen as
  // *content* is not cropped; a fixed 4:3 box otherwise, which reserves the
  // same space and crops rather than distorts. Either way the space exists
  // before the first byte arrives and nothing below jumps.
  const width = num(props.width_px)
  const height = num(props.height_px)
  return {
    heading: str(props.heading),
    body,
    points,
    image: source
      ? {
          src: source,
          // The firm's words when it wrote them. Empty — not invented — when it
          // did not: a made-up description read aloud is worse than silence.
          alt: str(props.image_alt) ?? '',
          width: width && height ? width : 640,
          height: width && height ? height : 480,
          cover: !(width && height),
        }
      : null,
    imageSide: props.image_side === 'left' ? 'left' : 'right',
    buttons: readButtons(props.buttons, ctx),
  }
}

export function SplitFeature(view: SplitFeatureView): ReactNode {
  const { body, points, image, imageSide, buttons } = view
  return (
    <Section
      className={`block split-feature split-${imageSide}${image ? '' : ' split-plain'}`}
      heading={view.heading}
    >
      <div className="split-inner">
        <div className="split-copy">
          {body ? (
            <div className="prose">
              <RichTextBody value={body} />
            </div>
          ) : null}
          {points.length > 0 ? (
            <ul className="split-points">
              {points.map((point, i) => (
                <li key={i}>{point}</li>
              ))}
            </ul>
          ) : null}
          <Buttons className="split-actions" buttons={buttons} />
        </div>
        {image ? (
          <div className={`split-art${image.cover ? ' split-art-cover' : ''}`}>
            <img
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading="lazy"
              decoding="async"
            />
          </div>
        ) : null}
      </div>
    </Section>
  )
}

/* ── product_spotlight ─────────────────────────────────────────────────── */

export interface SpotlightItem {
  title: string
  summary: string | null
  link: string | null
  image: string | null
  /** Formatted, or `null` — and `null` whenever FR-I4 hides prices. */
  price: string | null
  priceNote: string | null
}

export interface SpotlightView {
  heading: string | null
  intro: string | null
  layout: 'alternating' | 'stacked'
  items: SpotlightItem[]
}

/**
 * **The catalog, as the service resolved it**, at length rather than in a grid.
 *
 * The block stores slugs and nothing else about a service (ADR-0022 §5), so
 * every word here came from `prodesk_services` on this render. The price rule
 * is `readServices`'s, in the same words and for the same reason: a template
 * that re-read raw props could show a fee a regulator forbids, so the price
 * never leaves this function when FR-I4 says it may not.
 */
export function readSpotlight(props: Record<string, unknown>, ctx: BlockContext): SpotlightView | null {
  // Two guards, as `blog_list` reads `count` twice. The service has already
  // dropped the price from the payload when either says no; reading both here
  // is what makes the rule testable from this side, and what protects a
  // payload built from a fixture or a sample.
  const showPrices = ctx.showPricing && props.show_prices !== false
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const title = str(item.name) ?? str(item.title)
      if (!title) return null
      const price = obj(item.price)
      return {
        title,
        summary: str(item.description) ?? str(item.summary),
        link: link(item.href, ctx),
        image: src(item.image_url),
        price: showPrices ? rupees(price.amount ?? item.price) : null,
        priceNote: showPrices
          ? (str(price.billing_cadence)?.replace(/_/g, ' ') ?? str(item.price_note))
          : null,
      }
    })
    .filter((i): i is SpotlightItem => i !== null)
  // Every chosen slug has since been archived, or the firm chose none that
  // resolve. Nothing to spotlight is nothing to draw.
  if (items.length === 0) return null
  return {
    heading: str(props.heading),
    intro: str(props.intro),
    layout: props.layout === 'stacked' ? 'stacked' : 'alternating',
    items,
  }
}

export function Spotlight(view: SpotlightView): ReactNode {
  const { intro, items, layout } = view
  return (
    // **No `id="services"`.** That anchor is the `services` section's, and a
    // page carrying both would emit it twice — a duplicate id, where a link to
    // `#services` reaches whichever came first and the other is unreachable.
    // The same defect the two enquiry forms had, one section along (T-4.28).
    <Section className={`block spotlight spotlight-${layout}`} heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="spotlight-list">
        {items.map((item, i) => (
          <li className="spotlight-item" key={i}>
            {item.image ? (
              // Wave 3 close §6.1 — a 16:9 box, reserved before the bytes.
              <div className="spotlight-art">
                <img
                  src={item.image}
                  alt=""
                  width={640}
                  height={360}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
            <div className="spotlight-copy">
              <h3>{item.link ? <a href={item.link}>{item.title}</a> : item.title}</h3>
              {item.summary ? <p>{item.summary}</p> : null}
              {item.price ? (
                <p className="price">
                  {item.price}
                  {item.priceNote ? <span className="price-note"> {item.priceNote}</span> : null}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ── feature_grid ──────────────────────────────────────────────────────── */

export interface FeatureGridView {
  heading: string | null
  intro: string | null
  items: FeatureItem[]
}

/** C23, ADR-0045 §4: `icon` is the firm's pick, or `null` — never ours. */
export interface FeatureItem {
  title: string
  body: string | null
  href: string | null
  icon: string | null
}

export function readFeatureGrid(
  props: Record<string, unknown>,
  ctx: BlockContext,
): FeatureGridView | null {
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const title = str(item.title)
      if (!title) return null
      return { title, body: str(item.body), href: link(item.href, ctx), icon: iconKey(item.icon) }
    })
    .filter((i): i is FeatureItem => i !== null)
  if (items.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), items }
}

/**
 * **An icon only where the firm picked one.** ADR-0022 §4 refused one here —
 * a gavel we put beside "Commercial disputes" would be our editorial voice on
 * a regulated professional's website — and ADR-0045 §4 keeps exactly that
 * refusal while letting **the firm** pick its own, from a dropdown.
 */
export function FeatureGrid(view: FeatureGridView): ReactNode {
  const { intro, items } = view
  return (
    <Section className="block feature-grid" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="cards">
        {items.map((item, i) => (
          <li className="card" key={i}>
            <SiteIcon name={item.icon} className="card-icon" />
            <h3>{item.href ? <a href={item.href}>{item.title}</a> : item.title}</h3>
            {item.body ? <p>{item.body}</p> : null}
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ── logo_strip ────────────────────────────────────────────────────────── */

export interface LogoStripView {
  heading: string | null
  names: string[]
}

/** Suppressed entirely unless `show_client_names` is exactly `true`. */
export function readLogoStrip(props: Record<string, unknown>, ctx: BlockContext): LogoStripView | null {
  if (!allowed(ctx, 'show_client_names')) return null
  const names = list(props.names)
    .map((raw) => str(obj(raw).name))
    .filter((n): n is string => n !== null)
  if (names.length === 0) return null
  return { heading: str(props.heading), names }
}

/**
 * Names set as type, never a client's mark. A trademark is the client's
 * property and ProDesk holds no record of permission to put one on a
 * practice's website — spec §9-43, and §9-42 is the same question about the
 * name itself.
 */
export function LogoStrip(view: LogoStripView): ReactNode {
  return (
    <Section className="block logo-strip" heading={view.heading}>
      <ul className="logo-names">
        {view.names.map((name, i) => (
          <li key={i}>{name}</li>
        ))}
      </ul>
    </Section>
  )
}

/* ── testimonial_strip ─────────────────────────────────────────────────── */

export interface TestimonialView {
  heading: string | null
  intro: string | null
  items: { quote: string; name: string | null; role: string | null; organisation: string | null }[]
}

/** Suppressed entirely unless `show_testimonials` is exactly `true`. */
export function readTestimonials(
  props: Record<string, unknown>,
  ctx: BlockContext,
): TestimonialView | null {
  if (!allowed(ctx, 'show_testimonials')) return null
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const quote = str(item.quote)
      if (!quote) return null
      return {
        quote,
        name: str(item.name),
        role: str(item.role),
        organisation: str(item.organisation),
      }
    })
    .filter((t): t is TestimonialView['items'][number] => t !== null)
  if (items.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), items }
}

/**
 * **No rating and no photograph.** A numeral beside a quote implies a
 * measurement nobody has made, and this product has no object a star could be
 * computed from (ADR-0022 §4).
 */
export function Testimonials(view: TestimonialView): ReactNode {
  const { intro, items } = view
  return (
    <Section className="block testimonials" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="quote-list">
        {items.map((t, i) => (
          <li key={i}>
            <figure>
              <blockquote>{t.quote}</blockquote>
              {t.name || t.role || t.organisation ? (
                <figcaption>
                  {t.name ? <strong>{t.name}</strong> : null}
                  {[t.role, t.organisation].filter(Boolean).join(', ') || null}
                </figcaption>
              ) : null}
            </figure>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ── statement ─────────────────────────────────────────────────────────── */

export interface StatementView {
  eyebrow: string | null
  headline: string
}

export function readStatement(props: Record<string, unknown>): StatementView | null {
  const headline = str(props.headline)
  if (!headline) return null
  return { eyebrow: str(props.eyebrow), headline }
}

/** One line, on its own. It asks for nothing — the band that asks is `cta`. */
export function Statement(view: StatementView): ReactNode {
  return (
    <section className="block statement">
      <div className="wrap">
        {view.eyebrow ? <p className="eyebrow">{view.eyebrow}</p> : null}
        <p className="statement-line">{view.headline}</p>
      </div>
    </section>
  )
}

/* ── steps ─────────────────────────────────────────────────────────────── */

export interface StepsView {
  heading: string | null
  intro: string | null
  items: StepItem[]
}

/** C23, ADR-0045 §4: `icon` is the firm's pick, or `null` — never ours. */
export interface StepItem {
  title: string
  body: string | null
  icon: string | null
}

export function readSteps(props: Record<string, unknown>): StepsView | null {
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const title = str(item.title)
      return title ? { title, body: str(item.body), icon: iconKey(item.icon) } : null
    })
    .filter((s): s is StepItem => s !== null)
  if (items.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), items }
}

/**
 * **The number is the position.** An `<ol>`, numbered by the browser — so
 * reordering the steps in the builder reorders the numbers, and there is no
 * way for a page to end up reading "3, 1, 2". A typed ordinal guarantees that
 * eventually, on a page nobody re-reads.
 */
export function Steps(view: StepsView): ReactNode {
  const { intro, items } = view
  return (
    <Section className="block steps" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ol className="step-list">
        {items.map((step, i) => (
          <li key={i}>
            <SiteIcon name={step.icon} className="step-icon" />
            <h3>{step.title}</h3>
            {step.body ? <p>{step.body}</p> : null}
          </li>
        ))}
      </ol>
    </Section>
  )
}

/* ── story_strip ───────────────────────────────────────────────────────── */

export interface StoryStripView {
  heading: string | null
  intro: string | null
  items: { title: string; body: string; client: string | null }[]
}

/** Suppressed entirely unless `show_client_names` is exactly `true`. */
export function readStoryStrip(
  props: Record<string, unknown>,
  ctx: BlockContext,
): StoryStripView | null {
  if (!allowed(ctx, 'show_client_names')) return null
  const items = list(props.items)
    .map((raw) => {
      const item = obj(raw)
      const title = str(item.title)
      const body = str(item.body)
      // A story is the account, not the heading over it.
      return title && body ? { title, body, client: str(item.client_name) } : null
    })
    .filter((s): s is { title: string; body: string; client: string | null } => s !== null)
  if (items.length === 0) return null
  return { heading: str(props.heading), intro: str(props.intro), items }
}

/**
 * Work the firm has done, in the firm's own sentences. **No figure in a
 * callout beside them**: a number presented as a result reads as something
 * measured, and nothing here measured it (ADR-0022 §4).
 */
export function StoryStrip(view: StoryStripView): ReactNode {
  const { intro, items } = view
  return (
    <Section className="block stories" heading={view.heading}>
      {intro ? <p className="section-intro">{intro}</p> : null}
      <ul className="cards">
        {items.map((story, i) => (
          <li className="card story" key={i}>
            <h3>{story.title}</h3>
            {story.client ? <p className="story-client">{story.client}</p> : null}
            <p>{story.body}</p>
          </li>
        ))}
      </ul>
    </Section>
  )
}

/* ── lead_form ─────────────────────────────────────────────────────────── */

export interface LeadFormView {
  heading: string | null
  intro: string | null
  enquiry: EnquiryFormProps
}

/**
 * FR-I6's enquiry form, in a second place on the page — **not a second capture
 * path** (T-4.28). Same endpoint, same honeypot, same rate limit, same lead.
 *
 * `null` when there is nowhere to post to, which is 3D's rule and the one this
 * section cannot be allowed to break: the whole section *is* the form, so a
 * payload that predates `forms.enquiry` renders nothing here rather than a
 * heading over a form that silently drops an enquiry.
 */
export function readLeadForm(
  props: Record<string, unknown>,
  ctx: BlockContext,
  index: number,
): LeadFormView | null {
  const enquiry = enquiryProps(
    // `show_form` belongs to the `contact` block and this section has no such
    // field: a `lead_form` on a page is a firm asking for the form.
    { ...props, show_form: true },
    ctx.enquiryAction,
    ctx.pagePath,
    // No email fallback of its own — the failure notice offers the firm's
    // address, and this block does not carry one to offer.
    null,
    { idPrefix: `enq${index}`, notices: ctx.noticesAtIndex === undefined || ctx.noticesAtIndex === index },
  )
  if (!enquiry) return null
  return { heading: str(props.heading), intro: str(props.intro), enquiry }
}

export function LeadForm(view: LeadFormView): ReactNode {
  return (
    <Section className="block lead-form" heading={view.heading}>
      {view.intro ? <p className="section-intro">{view.intro}</p> : null}
      <EnquiryForm {...view.enquiry} />
    </Section>
  )
}

/* ── cta ───────────────────────────────────────────────────────────────── */

export interface CtaView {
  heading: string
  body: string | null
  /** C20, ADR-0041 §1 — primary then secondary; `[]` draws none. */
  buttons: SiteButton[]
}

export function readCta(props: Record<string, unknown>, ctx: BlockContext): CtaView | null {
  const heading = str(props.heading)
  if (!heading) return null
  return {
    heading,
    body: str(props.body),
    buttons: readButtons(props.buttons, ctx),
  }
}

/** The band that asks. No form — that is `lead_form` — and no colour variant. */
export function CtaBand(view: CtaView): ReactNode {
  const { body, buttons } = view
  return (
    <section className="block cta-band">
      <div className="wrap">
        <h2>{view.heading}</h2>
        {body ? <p>{body}</p> : null}
        <Buttons className="cta-actions" buttons={buttons} />
      </div>
    </section>
  )
}
